import React, { useState, useEffect } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { updateUser, getAllRoles } from '../../services/userService';
import { getAllGroups } from '../../services/groupService';
import { getDistricts, getNeighborhoods } from '../../services/addressService';
import { SuccessCircle, WarningIcon } from '../../assets/icons';
import Spinner from '../common/Spinner';
import RoleBadge from '../common/RoleBadge';
import useModalClose from '../../hooks/useModalClose';

const STEPS = {
  FORM: 0,
  CONFIRM: 1,
  COMPLETE: 2
};

// Role id for Denetleyici - check by id since name can change
const DENETLEYICI_ROLE_ID = 6;

const parseApiList = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.Data)) return response.Data;
  return [];
};

const parseMahlistToOptions = (mahlist) => {
  if (!mahlist || typeof mahlist !== 'string') return [];
  return mahlist
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((name) => ({ id: name, name }));
};

const EditUserModal = ({ isOpen, onClose, user, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(STEPS.FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isRolesLoading, setIsRolesLoading] = useState(true);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState([]);
  const [isDistrictLoading, setIsDistrictLoading] = useState(false);
  const [isNeighborhoodLoading, setIsNeighborhoodLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    tcnumber: '',
    email: '',
    grup: '',
    phoneNumber: '05',
    role: '1',
    ilce: '',
    mahalle: []
  });
  const [updatedUser, setUpdatedUser] = useState(null);

  const isDenetleyici = formData.role === String(DENETLEYICI_ROLE_ID);
  // Show ilce/mahalle if Denetleyici OR user has ilce/mahlist (legacy or non-Denetleyici with location data)
  const showIlceMahalle = isDenetleyici || !!(user?.ilce || user?.mahlist);

  useEffect(() => {
    if (user) {
      const mahalleFromUser = user.mahlist ? parseMahlistToOptions(user.mahlist) : (user.mahalle || []);
      setFormData({
        name: user.name,
        surname: user.surname,
        tcnumber: user.tcnumber,
        email: user.email,
        grup: user.grup,
        phoneNumber: user.phoneNumber || '05',
        role: String(user.role ?? user.roleId ?? '1'),
        ilce: user.ilce || '',
        mahalle: Array.isArray(mahalleFromUser)
          ? mahalleFromUser.map((m) => (typeof m === 'object' ? m : { id: m, name: m }))
          : []
      });
    }
  }, [user]);

  const resetState = () => {
    setCurrentStep(STEPS.FORM);
    setIsLoading(false);
    setError(null);
    setUpdatedUser(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleClose = () => {
    resetState();
    onClose();
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [groupsData, rolesData] = await Promise.all([
          getAllGroups(),
          getAllRoles()
        ]);
        setGroups(groupsData || []);
        setRoles(rolesData || []);
      } catch (err) {
        console.error('Failed to fetch groups or roles:', err);
      } finally {
        setIsRolesLoading(false);
      }
    };
    fetchData();
  }, []);

  // When hiding ilce/mahalle (no longer Denetleyici and user had no prior data), clear
  useEffect(() => {
    if (!showIlceMahalle) {
      setFormData((prev) => ({ ...prev, ilce: '', mahalle: [] }));
      setNeighborhoodOptions([]);
    }
  }, [showIlceMahalle]);

  // Fetch districts when ilce/mahalle section is shown
  useEffect(() => {
    if (!showIlceMahalle) return;
    const fetchDistricts = async () => {
      try {
        setIsDistrictLoading(true);
        const response = await getDistricts();
        const list = parseApiList(response);
        const normalized = list
          .map((item) => ({
            id: String(item.id ?? item.Id ?? item.ilceId ?? item.code ?? item.value ?? item.ad ?? ''),
            name: item.ad ?? item.name ?? item.IlceAd ?? item.ilceAd ?? item.label ?? item.id ?? ''
          }))
          .filter((item) => item.id && item.name);
        setDistrictOptions(normalized);
      } catch (err) {
        console.error('Failed to fetch districts:', err);
        setDistrictOptions([]);
      } finally {
        setIsDistrictLoading(false);
      }
    };
    fetchDistricts();
  }, [showIlceMahalle]);

  // Resolve ilce name to id when districts load (user.ilce may be "BORNOVA" etc)
  // NOTE: formData.ilce is intentionally excluded from deps — including it would reset
  // the selection back to the original district every time the user picks a new one.
  useEffect(() => {
    if (!user?.ilce || !districtOptions.length) return;
    const found = districtOptions.find(
      (d) =>
        String(d.name || '').toLowerCase() === String(user.ilce || '').toLowerCase() ||
        d.id === user.ilce
    );
    if (found) {
      setFormData((prev) => {
        if (prev.ilce === found.id) return prev;
        return { ...prev, ilce: found.id };
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districtOptions, user?.ilce]);

  // Fetch neighborhoods when ilce is selected
  useEffect(() => {
    if (!showIlceMahalle || !formData.ilce) {
      setNeighborhoodOptions([]);
      return;
    }
    const fetchNeighborhoods = async () => {
      try {
        setIsNeighborhoodLoading(true);
        const response = await getNeighborhoods(formData.ilce);
        const list = parseApiList(response);
        const normalized = list
          .map((item) => ({
            id: String(item.id ?? item.Id ?? item.mahalleId ?? item.code ?? item.value ?? item.ad ?? item.name ?? ''),
            name: item.ad ?? item.name ?? item.MahalleAd ?? item.mahalleAd ?? item.label ?? item.id ?? ''
          }))
          .filter((item) => item.id && item.name);
        setNeighborhoodOptions(normalized);
      } catch (err) {
        console.error('Failed to fetch neighborhoods:', err);
        setNeighborhoodOptions([]);
      } finally {
        setIsNeighborhoodLoading(false);
      }
    };
    fetchNeighborhoods();
  }, [showIlceMahalle, formData.ilce]);

  const handleGroupChange = (userId, newGroupName) => {
    setFormData((prev) => ({
      ...prev,
      grup: newGroupName,
    }));
  };

  const handleIlceChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, ilce: value, mahalle: [] }));
  };

  const handleMahalleChange = (e) => {
    setFormData((prev) => ({ ...prev, mahalle: e.value || [] }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phoneNumber') {
      // If the value is empty or less than 2 characters, reset to '05'
      if (value.length < 2) {
        setFormData(prev => ({
          ...prev,
          [name]: '05'
        }));
        return;
      }

      // Only allow numbers
      const numbersOnly = value.replace(/\D/g, '');
      
      // If the number doesn't start with '05', force it
      if (!numbersOnly.startsWith('05')) {
        const correctedNumber = '05' + numbersOnly.slice(2);
        setFormData(prev => ({
          ...prev,
          [name]: correctedNumber.slice(0, 11)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: numbersOnly.slice(0, 11)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (!formData.name || !formData.surname || !formData.tcnumber || !formData.phoneNumber || !formData.role) {
      setError('Lütfen tüm alanları doldurunuz.');
      return false;
    }
    if (isDenetleyici) {
      if (!formData.ilce) {
        setError('Denetleyici için ilçe seçiniz.');
        return false;
      }
      if (!formData.mahalle?.length) {
        setError('Denetleyici için en az bir mahalle seçiniz.');
        return false;
      }
    }
    if (formData.tcnumber.length !== 11 || !/^\d+$/.test(formData.tcnumber)) {
      setError('Geçerli bir TC Kimlik Numarası giriniz.');
      return false;
    }
    // Check if TC number ends with an even digit
    const lastDigit = parseInt(formData.tcnumber.slice(-1));
    if (lastDigit % 2 !== 0) {
      setError('TC Kimlik Numarası çift rakam ile bitmelidir.');
      return false;
    }

    // Phone validation
    if (formData.phoneNumber.length !== 11 || !formData.phoneNumber.startsWith('05')) {
      setError('Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır.');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    if (validateForm()) {
      setCurrentStep(STEPS.CONFIRM);
    }
  };

  const handleBack = () => {
    setCurrentStep(STEPS.FORM);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    setCurrentStep(STEPS.COMPLETE);

    try {
      const payload = { ...formData, isActive: true };
      if (showIlceMahalle && formData.mahalle?.length > 0) {
        payload.mahlist = formData.mahalle.map((m) => (typeof m === 'object' ? m.name : m)).join(',');
      }
      await updateUser(user.id, payload);
      const updatedUserData = {
        ...user,
        ...formData,
        role: parseInt(formData.role)
      };
      setUpdatedUser(updatedUserData);
      onSuccess(updatedUserData);
    } catch (error) {
      setError('Kullanıcı güncellenirken bir hata oluştu.');
      console.error('Update user error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="stepper">
      <div className={`step ${currentStep >= STEPS.FORM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.FORM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Kişi Bilgileri</span>
      </div>
      <div className={`step ${currentStep >= STEPS.CONFIRM ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.CONFIRM && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Bilgi Onayı</span>
      </div>
      <div className={`step ${currentStep >= STEPS.COMPLETE ? 'active' : ''}`}>
        <div className="step-circle">
          {currentStep >= STEPS.COMPLETE && <div className='step-active-circle' />}
        </div>
        <span className="step-title">Tamamlandı</span>
      </div>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="create-user-form">
      <div className="form-group">
        <label htmlFor="name">Ad</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Ad"
        />
      </div>
      <div className="form-group">
        <label htmlFor="surname">Soyad</label>
        <input
          type="text"
          id="surname"
          name="surname"
          value={formData.surname}
          onChange={handleInputChange}
          placeholder="Soyad"
        />
      </div>
      <div className="form-group">
        <label htmlFor="tcnumber">TC Kimlik Numarası</label>
        <input
          type="text"
          id="tcnumber"
          name="tcnumber"
          value={formData.tcnumber}
          onChange={handleInputChange}
          placeholder="TC Kimlik Numarası"
          maxLength="11"
        />
      </div>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Email"
        />
      </div>
      <div className="form-group">
        <label htmlFor="phoneNumber">Telefon Numarası</label>
        <input
          type="tel"
          id="phoneNumber"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={handleInputChange}
          placeholder="05XXXXXXXXX"
          maxLength="11"
        />
        <span className="helper-text">Örnek: 05XXXXXXXXX (11 haneli)</span>
      </div>
      <div className="form-group" style={{display: 'none'}}>
        <label htmlFor="grup">Grup</label>
<select id="grup"
          name="grup"
                  value={formData.grup || ""}
                  onChange={(e) => handleGroupChange(formData.id, e.target.value)}
                 
                >
                  <option value="">Seçiniz</option>
                  {groups.map((group) => (
                    <option key={group.name} value={group.name}>
                      {group.name}
                    </option>
                  ))}
                </select>
      </div>
      <div className="form-group">
        <label htmlFor="role">Rol</label>
        <select
          id="role"
          name="role"
          value={formData.role}
          onChange={handleInputChange}
          disabled={isRolesLoading}
        >
          <option value="">{isRolesLoading ? 'Yükleniyor...' : 'Seçiniz'}</option>
          {roles.map((role) => (
            <option key={role.id} value={String(role.id)}>
              {role.name}
            </option>
          ))}
        </select>
      </div>

      {showIlceMahalle && (
        <>
          <div className="form-group">
            <label htmlFor="ilce">İlçe</label>
            <select
              id="ilce"
              name="ilce"
              value={formData.ilce}
              onChange={handleIlceChange}
              disabled={isDistrictLoading}
              className="visit-filter-select"
            >
              <option value="">{isDistrictLoading ? 'Yükleniyor...' : 'Seçiniz'}</option>
              {districtOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="mahalle">Mahalle</label>
            <MultiSelect
              id="mahalle"
              value={formData.mahalle}
              options={neighborhoodOptions}
              onChange={handleMahalleChange}
              optionLabel="name"
              placeholder="Mahalle Seçiniz"
              disabled={!formData.ilce || isNeighborhoodLoading || neighborhoodOptions.length === 0}
              filter
              display="chip"
              maxSelectedLabels={3}
              className="mahalle-multiselect"
            />
            {!formData.ilce && (
              <p className="modal-helper-text">Önce ilçe seçiniz.</p>
            )}
            {isNeighborhoodLoading && (
              <p className="modal-helper-text">Mahalleler yükleniyor...</p>
            )}
            {formData.mahalle?.length > 0 && (
              <p className="modal-helper-text">{formData.mahalle.length} mahalle seçildi.</p>
            )}
          </div>
        </>
      )}

      {error && <div className="error-message">{error}</div>}
      <div className="form-actions">
        <button type="submit" className="submit-button">
            Düzenlemeyi Kaydet
        </button>
      </div>
    </form>
  );

  const renderConfirmation = () => {
    const ilceName = formData.ilce ? districtOptions.find((d) => d.id === formData.ilce)?.name : '';
    const mahalleNames = formData.mahalle?.map((m) => (typeof m === 'object' ? m.name : m)).join(', ') || '';
    return (
    <div className="confirmation-step">
      <WarningIcon />
      <h3>Kişi bilgilerini güncellemek üzeresiniz. Onaylıyor musunuz?</h3>
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Ad Soyad</th>
              <th>TC Kimlik No</th>
              <th>Email</th>
              <th>Telefon</th>
              <th>Grup</th>
              <th>Rol</th>
              {showIlceMahalle && (
                <>
                  <th>İlçe</th>
                  <th>Mahalle</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{`${formData.name} ${formData.surname}`}</td>
              <td>{formData.tcnumber}</td>
              <td>{formData.email}</td>
              <td>{formData.phoneNumber}</td>
              <td>{formData.grup}</td>
              <td><RoleBadge role={roles.find((r) => String(r.id) === formData.role)?.name ?? formData.role} /></td>
              {showIlceMahalle && (
                <>
                  <td>{ilceName}</td>
                  <td>{mahalleNames}</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="form-actions">
        <button type="button" onClick={handleBack} className="back-button">
          İptal Et
        </button>
        <button type="button" onClick={handleConfirm} className="confirm-button">
          Sonraki Adım
        </button>
      </div>
    </div>
    );
  };

  const renderComplete = () => (
    <div className="complete-step">
      {isLoading ? (
        <div className="loading-state">
          <Spinner size="large" />
          <h3>Süreciniz tamamlanıyor...</h3>
          <button type="button" onClick={handleClose} className="cancel-button">
            Süreci İptal Et
          </button>
        </div>
      ) : updatedUser ? (
        <div className="success-state">
          <SuccessCircle />
          <h3>Süreciniz tamamlandı. Kişi bilgileri güncellendi.</h3>
          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Ad Soyad</th>
                  <th>TC Kimlik No</th>
                  <th>Email</th>
                  <th>Telefon</th>
                  <th>Grup</th>
                  <th>Rol</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{`${updatedUser.name} ${updatedUser.surname}`}</td>
                  <td>{updatedUser.tcnumber}</td>
                  <td>{updatedUser.email}</td>
                  <td>{updatedUser.phoneNumber}</td>
                  <td>{updatedUser.grup}</td>
                  <td><RoleBadge role={roles.find((r) => String(r.id) === String(updatedUser.role))?.name ?? updatedUser.role} /></td>
                </tr>
              </tbody>
            </table>
          </div>
          <button type="button" onClick={handleClose} className="close-button">
            Kapat
          </button>
        </div>
      ) : (
        <div className="error-state">
          <div className="error-icon">✕</div>
          <h3>Hata Oluştu</h3>
          <p>{error}</p>
          <button type="button" onClick={handleBack} className="retry-button">
            Tekrar Dene
          </button>
        </div>
      )}
    </div>
  );

  // Add modal closing behaviors
  useModalClose(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Kişi Bilgilerini Düzenle</h3>
        </div>
        {renderStepIndicator()}
        <div className="modal-body">
          {currentStep === STEPS.FORM && renderForm()}
          {currentStep === STEPS.CONFIRM && renderConfirmation()}
          {currentStep === STEPS.COMPLETE && renderComplete()}
        </div>
      </div>
    </div>
  );
};

export default EditUserModal; 